import type { ReactNode } from "react";
import { Check, CheckCircle2, History, Info } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { formatDateTime } from "@/utils/format";
import {
  TAREFA_TIMELINE_LABELS,
  type TarefaDto,
  type TarefaTimelineEvent,
  type TarefaTimelineEventType,
} from "@/services/tarefas";

interface Props {
  open: boolean;
  onClose: () => void;
  tarefa: TarefaDto;
}

type NoteVariant = "info" | "success" | "warning";

function noteVariant(type: TarefaTimelineEventType): NoteVariant | null {
  if (type === "concluida") return "success";
  if (type === "criada" || type === "editada" || type === "compartilhamento_alterado") return "info";
  if (type === "status_alterado") return "warning";
  return null;
}

function TimelineNoteBox({
  variant,
  children,
}: {
  variant: NoteVariant;
  children: ReactNode;
}) {
  if (variant === "success") {
    return (
      <div className="mt-2.5 flex items-start gap-2.5 rounded-xl border border-emerald-100 bg-emerald-50 px-3.5 py-2.5">
        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" strokeWidth={2.5} />
        <p className="text-sm leading-snug text-emerald-800">{children}</p>
      </div>
    );
  }
  if (variant === "warning") {
    return (
      <div className="mt-2.5 flex items-start gap-2.5 rounded-xl border border-amber-100 bg-amber-50 px-3.5 py-2.5">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" strokeWidth={2.5} />
        <p className="text-sm leading-snug text-amber-900">{children}</p>
      </div>
    );
  }
  return (
    <div className="mt-2.5 flex items-start gap-2.5 rounded-xl border border-sky-100 bg-sky-50 px-3.5 py-2.5">
      <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-600" strokeWidth={2.5} />
      <p className="text-sm leading-snug text-sky-900">{children}</p>
    </div>
  );
}

function TimelineStep({
  event,
  isLast,
}: {
  event: TarefaTimelineEvent;
  isLast: boolean;
}) {
  const label = TAREFA_TIMELINE_LABELS[event.type] ?? event.type;
  const boxVariant = event.note ? noteVariant(event.type) : null;

  return (
    <li className="relative flex gap-4">
      <div className="flex w-5 flex-shrink-0 flex-col items-center">
        {isLast ? (
          <div
            className="relative z-10 h-5 w-5 flex-shrink-0 rounded-full bg-brand-700 ring-4 ring-white"
            aria-hidden
          />
        ) : (
          <div
            className="relative z-10 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 ring-4 ring-white"
            aria-hidden
          >
            <Check className="h-3 w-3 text-sky-600" strokeWidth={3} />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1 pb-7">
        <p className="text-sm font-bold leading-snug text-slate-900">{label}</p>
        <p className="mt-1 text-xs text-slate-500">
          {formatDateTime(event.at)} • {event.userName}
        </p>
        {event.note && boxVariant && <TimelineNoteBox variant={boxVariant}>{event.note}</TimelineNoteBox>}
        {event.note && !boxVariant && (
          <p className="mt-2 rounded-xl border border-slate-100 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-600">
            {event.note}
          </p>
        )}
      </div>
    </li>
  );
}

export function TarefaTimelineModal({ open, onClose, tarefa }: Props) {
  const events = [...(tarefa.timeline ?? [])].sort(
    (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime(),
  );

  const modalTitle = (
    <div className="flex min-w-0 items-center gap-3">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-red-50">
        <History className="h-4 w-4 text-brand-700" strokeWidth={2.25} />
      </div>
      <span className="truncate font-bold text-slate-900">Leadtime — {tarefa.titulo}</span>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      size="md"
      footer={
        <div className="flex w-full items-center justify-end">
          <Button type="button" onClick={onClose}>
            Fechar
          </Button>
        </div>
      }
    >
      {events.length === 0 ? (
        <p className="py-2 text-sm text-slate-500">Nenhum evento registrado.</p>
      ) : (
        <ol className="relative pl-0.5">
          {events.length > 1 && (
            <span
              className="absolute bottom-6 left-[10px] top-2.5 w-px bg-slate-200"
              aria-hidden
            />
          )}
          {events.map((ev, idx) => (
            <TimelineStep key={ev.id} event={ev} isLast={idx === events.length - 1} />
          ))}
        </ol>
      )}
    </Modal>
  );
}
